// [ai-refactored-code]
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '@services/admin/user.service';
import { GaragePassageService } from '@services/garage-passage.service';
import { GaragePassage } from '@/models/garage-passage.model';
import { GaragePassageFormMeta } from '@pages/garage-passages/garage-passage-detail-form/garage-passage-form-meta';
import { LoggerService } from '@services/logger.service';
import { Registry } from '@/registry.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CemCustomerModel } from '@/models/cem-customer.model';

@Component({
  selector: 'app-garage-passage-detail-form',
  templateUrl: '../../../components/dynamic-form/dynamic-form.component.html',
  styleUrls: ['../../../components/dynamic-form/dynamic-form.component.scss'],
})
export class GaragePassageDetailFormComponent extends DynamicFormComponent implements OnInit {
  @Input() data: GaragePassage;
  @Output() modalClose = new EventEmitter<GaragePassage>();

  constructor(
    protected toastr: ToastrService,
    protected userService: UserService,
    private garagePassageService: GaragePassageService,
  ) {
    super(toastr, userService);
    this.primaryButton = 'Salva';
    this.secondaryBottomButton = 'Annulla';
    this.submitTopButton = false;
  }

  ngOnInit() {
    // Log dei dati del dettaglio CEM nel form
    console.log('=== DETTAGLIO RECORD CEM - FORM ===');
    console.log('Dati ricevuti nel form:', this.data);
    console.log('====================================');
    LoggerService.log(this.data, 'Dettaglio record CEM - Dati nel form');

    this.refreshForm();

    this.setupConditionalLogic();

    // Registra il componente nel Registry per permettere all'autocomplete di chiamare i metodi
    Registry.getInstance().set('GaragePassageDetailFormComponent', this);
  }

  setupMeta(): void {
    const shouldEnableStatoConsegnaVgi = this.data?.reparto === 'VENDITE';
    const shouldEnableRivenditoreEsterno =
      this.data?.reparto === 'VENDITE' && this.data?.tipoVendita === 'ESTERNO';
    const isRepartoVenditaInterno =
      this.data?.reparto === 'VENDITE' && this.data?.tipoVendita === 'INTERNO';
    const isRepartoOfficina = this.data?.reparto === 'OFFICINA';
    this.meta = GaragePassageFormMeta.getMeta(
      shouldEnableStatoConsegnaVgi,
      shouldEnableRivenditoreEsterno,
      isRepartoVenditaInterno,
      isRepartoOfficina || isRepartoVenditaInterno,
    );
  }

  public onSubmit() {
    if (this.form.invalid) {
      this.markFormGroupTouched(this.form);
      this.toastr.error('Ci sono errori nel form. Controlla i campi email.');
      return;
    }

    this.submitButtonLoading = true;
    const formData = this.form.getRawValue();

    // Mappa i dati del form alla struttura corretta dell'API
    const updatedPassage: GaragePassage = {
      ...this.data, // Mantieni i dati originali
      ...formData, // Sovrascrivi con i dati del form
      // Assicura che i campi numerici siano numeri
      id: Number(formData.id) || this.data?.id || 0,
      numeroPratica: Number(formData.numeroPratica) || 0,
      nrBpCliente: Number(formData.nrBpCliente) || 0,
      nrBpUtilizzatore: Number(formData.nrBpUtilizzatore || formData.bpEmail) || 0,
      nrBpProprietario: Number(formData.nrBpProprietario) || 0,
      nrBpNotizia: Number(formData.nrBpNotizia) || 0,
      bpEmail: Number(formData.bpEmail) || 0,
      // Assicura che consensoMarketing sia boolean (gestisce anche il vecchio nome consensomarketing)
      consensoMarketing:
        formData.consensoMarketing ??
        formData.consensomarketing ??
        this.data?.consensoMarketing ??
        false,
      // Mappa denominazioneUtilizzatore (se viene da autocomplete, potrebbe essere un oggetto)
      denominazioneUtilizzatore:
        typeof formData.denominazioneUtilizzatore === 'string'
          ? formData.denominazioneUtilizzatore
          : formData.denominazioneEmail || this.data?.denominazioneUtilizzatore || '',
      // Mappa emailUtilizzatore
      emailUtilizzatore:
        formData.emailUtilizzatore || formData.email || this.data?.emailUtilizzatore || '',
      // Mantieni denominazioneEmail se presente
      denominazioneEmail:
        formData.denominazioneEmail ||
        formData.denominazioneUtilizzatore ||
        this.data?.denominazioneEmail ||
        '',
    };

    console.log('=== DATI INVIATI AL SAVE ===');
    console.log('Dati mappati per il save:', updatedPassage);
    console.log('=============================');
    LoggerService.log(updatedPassage, 'Dati inviati al save CEM');

    this.garagePassageService.updateGaragePassage(updatedPassage).subscribe({
      next: (response: GaragePassage) => {
        this.toastr.success('Record CEM aggiornato correttamente!');
        this.modalClose.emit(response);
      },
      error: (error) => {
        LoggerService.error('updateGaragePassage failed!');
        LoggerService.error(error);
        this.toastr.error("Errore durante l'aggiornamento del record CEM.");

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
          this.submitButtonLoading = false;
        }, 1000);
      },
      complete: () => {
        LoggerService.log('Request done');

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
          this.submitButtonLoading = false;
        }, 1000);
      },
    });
  }

  public bottomButtonAction() {
    this.modalClose.emit(this.data);
  }

  private setupConditionalLogic(): void {
    if (!this.form) {
      return;
    }
    this.updateRivenditoreEsternoState();
    // Whenever reparto or tipoVendita changes, update the state dynamically
    const repartoControl = this.form.get('reparto');
    const tipoVenditaControl = this.form.get('tipoVendita');
    if (repartoControl && tipoVenditaControl) {
      repartoControl.valueChanges.subscribe(() => this.updateRivenditoreEsternoState());
      tipoVenditaControl.valueChanges.subscribe(() => this.updateRivenditoreEsternoState());
    }
  }

  private updateRivenditoreEsternoState(): void {
    // Always ensure the control exists so that unit tests can reliably access it
    let rivenditoreEsternoControl = this.form.get('rivenditoreEsterno');
    if (!rivenditoreEsternoControl) {
      this.form.addControl('rivenditoreEsterno', new FormControl({ value: '', disabled: true }));
      rivenditoreEsternoControl = this.form.get('rivenditoreEsterno');
    }

    const shouldEnable = this.data?.reparto === 'VENDITE' && this.data?.tipoVendita === 'ESTERNO';

    if (shouldEnable) {
      // Enable the control and enforce the `required` validator
      rivenditoreEsternoControl.clearValidators();
      rivenditoreEsternoControl.setErrors(null);
      rivenditoreEsternoControl.enable({ emitEvent: false, onlySelf: true });
      rivenditoreEsternoControl.setValidators([Validators.required]);
      rivenditoreEsternoControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    } else {
      // When the field is not relevant: clear validators, reset value, disable control
      rivenditoreEsternoControl.clearValidators();
      rivenditoreEsternoControl.setErrors(null);
      rivenditoreEsternoControl.setValue('', { emitEvent: false });
      rivenditoreEsternoControl.disable({ emitEvent: false, onlySelf: true });
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Metodo chiamato quando viene selezionato un utilizzatore dall'autocomplete
   * Popola automaticamente i campi: emailCliente, denominazioneUtilizzatore, nrBpUtilizzatore, telefonoUtilizzatore
   */
  public onUtilizzatoreSelected(utilizzatore: CemCustomerModel) {
    if (!utilizzatore || !this.form) {
      return;
    }

    LoggerService.log(utilizzatore, 'Utilizzatore selezionato:');

    // Popola denominazioneUtilizzatore (campo autocomplete)
    const denominazioneUtilizzatoreControl = this.form.get('denominazioneUtilizzatore');
    if (denominazioneUtilizzatoreControl && utilizzatore.denominazione) {
      denominazioneUtilizzatoreControl.setValue(utilizzatore.denominazione, { emitEvent: false });
    }

    // Popola nrBpUtilizzatore (nel form è bpEmail)
    const bpEmailControl = this.form.get('bpEmail');
    if (bpEmailControl && utilizzatore.numeroBp) {
      bpEmailControl.setValue(utilizzatore.numeroBp, { emitEvent: false });
    }

    // Popola anche nrBpUtilizzatore nel modello (se esiste nel form)
    const nrBpUtilizzatoreControl = this.form.get('nrBpUtilizzatore');
    if (nrBpUtilizzatoreControl && utilizzatore.numeroBp) {
      nrBpUtilizzatoreControl.setValue(utilizzatore.numeroBp, { emitEvent: false });
    }

    // Popola emailUtilizzatore (campo email)
    const emailUtilizzatoreControl = this.form.get('email');
    if (emailUtilizzatoreControl && utilizzatore.email) {
      emailUtilizzatoreControl.setValue(utilizzatore.email, { emitEvent: false });
    }

    // Popola anche emailUtilizzatore nel modello (se esiste nel form)
    const emailUtilizzatoreModelControl = this.form.get('emailUtilizzatore');
    if (emailUtilizzatoreModelControl && utilizzatore.email) {
      emailUtilizzatoreModelControl.setValue(utilizzatore.email, { emitEvent: false });
    }

    // Popola telefonoUtilizzatore
    const telefonoUtilizzatoreControl = this.form.get('telefonoUtilizzatore');
    if (telefonoUtilizzatoreControl && utilizzatore.numeroTelefono) {
      telefonoUtilizzatoreControl.setValue(utilizzatore.numeroTelefono, { emitEvent: false });
    }

    LoggerService.log("Campi popolati con i dati dell'utilizzatore selezionato");
  }
}
